import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { resetPasswordApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { toastError } from "@/shared/lib/toast";
import { AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";
import { AuthHeading } from "@/shared/components/AuthHeading";

interface ResetPasswordState {
  email: string;
  resetToken: string;
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const state = location.state as ResetPasswordState | null;

  if (!state?.email || !state?.resetToken) {
    return <Navigate to="/patient/auth/forgot-password" replace />;
  }

  const { email, resetToken } = state;

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { password, confirmPassword },
      {
        password: [
          { required: true, message: "Password is required" },
          { min: 6, message: "Password must be at least 6 characters" },
        ],
        confirmPassword: [{ required: true, message: "Please confirm your password" }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    if (password !== confirmPassword) {
      toastError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const result = toAuthResult(await resetPasswordApi(email, resetToken, password, confirmPassword));
    setLoading(false);

    if (result.success) {
      navigate("/patient/auth/signin", {
        replace: true,
        state: { notice: "Password reset successfully! Please sign in." },
      });
    } else {
      toastError(result.error ?? "Unable to reset password.");
    }
  };

  return (
    <div className="w-full">
      <AuthHeading title={<>Reset your <span className="font-extrabold tracking-tight">password</span></>} subtitle="Enter New Password To Access Your Account." />

      <form onSubmit={handleSubmit} className="mt-[52px]">
        <AuthPasswordField
          variant="pill"
          icon="msq:password-key"
          label="New password"
          placeholder="Password"
          autoComplete="new-password"
          value={password}
          onChange={(v) => { setPassword(v); clearError("password"); }}
          error={errors.password}
          disabled={loading}
          className="mb-[22px]"
        />

        <AuthPasswordField
          variant="pill"
          icon="msq:password-key"
          label="Confirm password"
          placeholder="Confirm Password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(v) => { setConfirmPassword(v); clearError("confirmPassword"); }}
          error={errors.confirmPassword}
          disabled={loading}
          className="mb-[52px]"
        />

        <AuthSubmitButton loading={loading} className="!h-[43px] !py-0 !text-[15px] !font-medium">
          Reset Password
        </AuthSubmitButton>
      </form>
    </div>
  );
}
